import { inject, Injectable } from '@angular/core';
import {
  AppComms,
  DatadogService,
  DefaultOrgContext,
  DefaultStoreContext,
  IFrameService,
  Module,
  ModuleType,
  Organization,
  OrgStore,
  PendoService
} from '@enliven/angular';
import { noop, Observable, of, tap } from 'rxjs';
import { SELECTED_ORG } from '../constants/storage-constants';
import { E360SessionService } from './e360-session.service';
import { DataService } from './data.service';
import { E360UserService } from './e360-user.service';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private readonly datadogService: DatadogService = inject(DatadogService);
  private readonly dataService: DataService = inject(DataService);
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly e360UserService: E360UserService = inject(E360UserService);
  private readonly iframeService: IFrameService = inject(IFrameService);
  private readonly pendoService: PendoService = inject(PendoService);

  private _orgs: Organization[];
  private _orgStores: OrgStore[];

  private _selectedOrg: Organization;
  private _selectedOrgStore: OrgStore;

  get orgs(): Organization[] {
    return this._orgs;
  }

  set orgs(val: Organization[]) {
    this._orgs = val;
    if (val?.length && !this._selectedOrg) {
      this.determineSelectedOrg();
    }
  }

  get orgStores(): OrgStore[] {
    return this._orgStores;
  }

  set orgStores(val: OrgStore[]) {
    this._orgStores = val;
    if (val?.length) {
      this.determineSelectedStore(this.e360SessionService.defaultStoreContext, val[0]);
    }
  }

  get selectedOrg(): Organization {
    return this._selectedOrg;
  }

  set selectedOrg(val: Organization) {
    const current = this._selectedOrg?.organizationId;
    const next = val?.organizationId;
    this._selectedOrg = val;
    if (next && next !== current) {
      this.updateDefaultOrgIfNeeded();
      this.handleSelectedOrgChange(!!current);
    }
  }

  get selectedOrgStore(): OrgStore {
    return this._selectedOrgStore;
  }

  set selectedOrgStore(val: OrgStore) {
    const current = this._selectedOrgStore?.id;
    const next = val?.id;
    this._selectedOrgStore = val;
    if (next && next !== current) {
      this.handleSelectedStoreChange();
      this.updateDefaultStoreIfNeeded();
    }
    if (this.e360SessionService.session.sessionId) {
      // this is fired anytime an org or store is selected either via user change or e360 auto-selection
      // regardless, keep the DB session in sync with the UI
      this.e360SessionService.updateE360Session().subscribe(() => noop());
    }
  }

  clear() {
    this._orgs = [];
    this._orgStores = [];
    this._selectedOrg = null;
    this._selectedOrgStore = null;
  }

  // used to get the org information for a deep link scenario since _orgs
  // only contains 100 orgs at a time and global users may have more than that available
  selectOrgById(organizationId: string): Observable<Organization> {
    if (this._selectedOrg?.organizationId === organizationId) {
      return of(this._selectedOrg);
    }

    const orgFromList = this._orgs?.find(org => org.organizationId === organizationId);
    if (orgFromList) {
      this.selectedOrg = orgFromList;
      return of(orgFromList);
    }

    return this.dataService.getOrgFromOrgApi(organizationId).pipe(
      tap((org: Organization) => {
        this.addOrgToList(org);
        this.selectedOrg = org;
      })
    );
  }

  private addOrgToList(org: Organization) {
    const orgs = [...(this._orgs ?? []), org];
    orgs.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    this._orgs = orgs;
  }

  sendStateToPluginApps() {
    const { selectedOrg, selectedOrgStore } = this;
    this.iframeService.sendMessageToIFrame(AppComms.STATE_CHANGES, { selectedOrg, selectedOrgStore });
    // Below two lines are tech debt to be removed once all teams move to the above mechanism
    this.iframeService.sendMessageToIFrame(AppComms.ORG_CHANGED, selectedOrg);
    this.iframeService.sendMessageToIFrame(AppComms.ORG_STORE_CHANGED, selectedOrgStore);
  }

  private sendUpdatedUserToPlugInApps() {
    this.iframeService.sendMessageToIFrame(AppComms.PASS_USER_DETAILS, this.e360UserService.user);
  }

  private determineSelectedOrg() {
    const selectedOrg = JSON.parse(sessionStorage.getItem(SELECTED_ORG)); // refresh, new org added/edited
    const authenticatingOrg = this._orgs.find(org => org.organizationId === this.e360SessionService.orgId); // login
    this.selectedOrg = selectedOrg ?? authenticatingOrg; // these will always be the same until phase 2 changes IP-12015
  }

  private determineSelectedStore(defaultStoreContext: DefaultStoreContext, fallbackStore: OrgStore) {
    const { storeId, storeIdType } = defaultStoreContext || {};
    const defaultStore = this._orgStores.find(os =>
      storeId && storeIdType ? os[storeIdType] === storeId || os.organizationStoreId === storeId : false
    );
    this.selectedOrgStore = defaultStore || fallbackStore;
  }

  private handleSelectedOrgChange(isOrgSwitch: boolean = false) {
    sessionStorage.setItem(SELECTED_ORG, JSON.stringify(this._selectedOrg));
    this.datadogService.setOrganization(this._selectedOrg);
    if (isOrgSwitch) {
      const { visitor, organizationIdpExternalId } = this.e360SessionService.session;
      const user = this.e360UserService.user;

      this.pendoService.initialize({
        visitor: {
          id: visitor?.visitorId ?? '',
          email: user.adminEmail,
          firstName: user.firstName,
          lastName: user.lastName,
          enrolled: visitor?.enrolled ?? false
        },
        account: {
          id: organizationIdpExternalId ?? '',
          accountName: this._selectedOrg.name
        }
      });
    }
    this.dataService.getOrgById(this._selectedOrg.organizationId).subscribe(newSelectedOrgInfo => {
      this.e360UserService.currentOrgId = this._selectedOrg.organizationId;

      const newOrgModules = newSelectedOrgInfo.modules || [];
      // Only keep modules from the new org that the user has permission to access
      const userPermissionIds = new Set(
        this.e360UserService.user?.scopeRoles[0]?.role?.permissions?.map(p => p.id) ?? []
      );
      const newOrgModulesUserCanAccess = newOrgModules.filter(m =>
        m.permissions?.some(p => userPermissionIds.has(p.id))
      );

      if (this.e360UserService.isMultiOrgUser) {
        // reset modules for multi-org users
        this.e360UserService.user = {
          ...this.e360UserService.user,
          modules: newOrgModulesUserCanAccess
        };
      } else if (this.e360UserService.isGlobalUser) {
        // global users should have access to all orgs under users owning org plus new selected org
        let modules: Module[];

        if (this._selectedOrg.organizationId !== this.e360UserService.userOrgId) {
          const scopedModules =
            this.e360UserService.modules?.filter(
              m =>
                m.organizationId === this._selectedOrg.organizationId ||
                m.organizationId === this.e360UserService.userOrgId
            ) ?? [];

          // Only keep top level modules from the new org that the user has permission to access
          const newOrgTopModulesUserCanAccess = newOrgModulesUserCanAccess.filter(m => m.type === ModuleType.TOP) ?? [];

          modules = [...scopedModules, ...newOrgTopModulesUserCanAccess];
        } else {
          modules = newOrgModulesUserCanAccess ?? [];
        }

        this.e360UserService.user = {
          ...this.e360UserService.user,
          modules
        };
      }
      this.sendUpdatedUserToPlugInApps();
    });
  }

  private handleSelectedStoreChange() {
    this.datadogService.setOrgStore(this._selectedOrgStore);
  }

  private updateDefaultOrgIfNeeded() {
    const newSelectedOrgId: string = this._selectedOrg.organizationId;
    const shouldUpdateDefaultOrg: boolean =
      newSelectedOrgId !== this.e360SessionService.defaultOrganizationContext?.organizationId;
    if (shouldUpdateDefaultOrg && this.e360SessionService.session.sessionId) {
      const newDefaultOrg: DefaultOrgContext = {
        organizationId: this._selectedOrg.organizationId,
        organizationIdType: 'organizationId'
      };
      this.e360SessionService.setDefaultOrg(newDefaultOrg);
    }
  }

  private updateDefaultStoreIfNeeded() {
    const newSelectedStoreId: string = this._selectedOrgStore.organizationStoreId;
    const shouldUpdateDefaultStore: boolean =
      newSelectedStoreId !== this.e360SessionService.defaultStoreContext?.storeId;
    if (shouldUpdateDefaultStore && this.e360SessionService.session.sessionId) {
      const newDefaultStore: DefaultStoreContext = {
        storeId: this._selectedOrgStore.organizationStoreId,
        storeIdType: 'organizationstoreid'
      };
      this.e360SessionService.setDefaultStore(newDefaultStore);
    }
  }
}
